<template>
  <figure class="max-chart max-panel" :aria-label="title || undefined">
    <figcaption v-if="title" class="max-panel__title">{{ title }}</figcaption>
    <div
      v-show="!failed"
      class="max-chart__canvas"
      style="position: relative"
      :style="{ '--max-chart-height': `var(--max-chart-height-${node.height ?? 'md'})` }"
    >
      <div ref="canvasEl" role="img" :aria-label="compiled?.summary" style="position: absolute; inset: 0" />
      <div v-if="!ready" class="max-chart__skeleton" style="position: absolute; inset: 0" role="status" :aria-label="ctx.labels.loadingChart" />
    </div>
    <p v-if="failed" class="max-callout" data-intent="warning">{{ ctx.labels.chartFailed }}</p>
    <p v-if="node.caption" class="max-chart__caption">{{ node.caption }}</p>
    <button v-if="compiled" type="button" class="max-chart__toggle" :aria-expanded="showTable || failed" @click="showTable = !showTable">
      {{ showTable || failed ? ctx.labels.hideTable : ctx.labels.showTable }}
    </button>
    <div v-if="compiled && (showTable || failed)" class="max-table-wrap">
      <table class="max-table">
        <caption class="max-sr-only">{{ compiled.summary }}</caption>
        <thead>
          <tr>
            <th v-for="col in compiled.table.columns" :key="col.key" scope="col" :data-align="col.align">{{ col.label }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in compiled.table.rows" :key="i">
            <td v-for="col in compiled.table.columns" :key="col.key" :data-align="col.align">
              {{ formatValue(row[col.key], { format: col.format ?? (typeof row[col.key] === 'number' ? compiled.valueFormat.format : 'text'), currency: compiled.valueFormat.currency, locale: ctx.locale }) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </figure>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue'
import { compileChart, type CompiledChart } from '../core/charts.ts'
import '../core/charts-advanced.ts'
import { withFormatters } from '../core/chart-format.ts'
import { formatValue } from '../core/format.ts'
import { loadEcharts } from '../runtime/echarts.ts'
import { useMaxContext } from '../runtime/context.ts'

const props = defineProps<{ node: any }>()
const ctx = useMaxContext()

const canvasEl = ref<HTMLElement | null>(null)
const ready = ref(false)
const failed = ref(false)
const showTable = ref(false)
const title = computed(() => props.node.title ?? props.node.chart?.title)

function pickLabels (keys: string[]): Record<string, string> {
  const out: Record<string, string> = {}
  for (const key of keys) {
    if (typeof ctx.labels[key] === 'string') out[key] = ctx.labels[key] as string
  }
  return out
}

const compiled = computed<CompiledChart | null>(() => {
  try {
    return compileChart(props.node.chart, {
      preset: ctx.preset,
      locale: ctx.locale,
      reducedMotion: ctx.reducedMotion.value,
      labels: {
        category: ctx.labels.category,
        value: ctx.labels.value,
        share: ctx.labels.share,
        series: ctx.labels.series,
        severity: ctx.labels.severity,
        ...pickLabels(['from', 'to', 'date', 'open', 'close', 'low', 'high', 'conversion'])
      }
    })
  } catch {
    return null
  }
})

let chart: any = null
let resizeObserver: ResizeObserver | null = null
let visibilityObserver: IntersectionObserver | null = null

async function render () {
  const el = canvasEl.value
  const spec = compiled.value
  if (!el || !spec) {
    failed.value = !spec
    return
  }
  try {
    const echarts = await loadEcharts(spec.modules, spec.renderer)
    if (!canvasEl.value) return
    if (!chart || chart.getDom() !== el || chart.__maxRenderer !== spec.renderer) {
      chart?.dispose()
      chart = echarts.init(el, null, { renderer: spec.renderer })
      chart.__maxRenderer = spec.renderer
    }
    chart.setOption(withFormatters(spec, ctx.locale), true)
    ready.value = true
    failed.value = false
    resizeObserver ??= new ResizeObserver(() => chart?.resize())
    resizeObserver.observe(el)
  } catch (error) {
    console.error('[johku-max] chart render failed', error)
    failed.value = true
  }
}

onMounted(() => {
  const el = canvasEl.value
  if (!el) return
  if (typeof IntersectionObserver === 'undefined') {
    render()
    return
  }
  visibilityObserver = new IntersectionObserver((entries) => {
    if (entries.some(e => e.isIntersecting)) {
      visibilityObserver?.disconnect()
      visibilityObserver = null
      render()
    }
  }, { rootMargin: '200px' })
  visibilityObserver.observe(el)
})

watch(compiled, () => { if (ready.value || failed.value) render() })

onBeforeUnmount(() => {
  visibilityObserver?.disconnect()
  resizeObserver?.disconnect()
  chart?.dispose()
  chart = null
})
</script>
